/**
 * The data-transferred line under an interface chart.
 *
 * Decimal units (1 GB = 10^9 bytes), as carriers bill and as link speeds
 * are quoted -- not the binary GiB a file manager shows, which would make
 * a 1 Gbit/s link's day look 7% smaller than the invoice.
 */

export interface TrafficTotals {
  in_bytes: number | null;
  out_bytes: number | null;
  in_coverage: number;
  out_coverage: number;
}

const UNITS = ["B", "kB", "MB", "GB", "TB", "PB"];

export function formatBytes(bytes: number | null): string {
  if (bytes === null) return "—";
  let value = bytes;
  let unit = 0;
  while (value >= 1000 && unit < UNITS.length - 1) {
    value /= 1000;
    unit++;
  }
  // Three significant figures: 329 GB, 32.9 GB, 3.29 GB.
  const text = unit === 0 ? String(Math.round(value)) : value >= 100 ? value.toFixed(0) : value >= 10 ? value.toFixed(1) : value.toFixed(2);
  return `${text} ${UNITS[unit]}`;
}

/**
 * Below this, the window's total is said to cover only part of it. A few
 * missed polls in a day are not worth a caveat; a device added halfway
 * through the window is.
 */
const COMPLETE = 0.98;

export function totalsLabel(totals: TrafficTotals): { text: string; partial: string | null } {
  const text = `IN ${formatBytes(totals.in_bytes)} · OUT ${formatBytes(totals.out_bytes)}`;
  const coverage = Math.min(totals.in_coverage, totals.out_coverage);
  if (totals.in_bytes === null && totals.out_bytes === null) return { text, partial: null };
  if (coverage >= COMPLETE) return { text, partial: null };
  // Floored, so 97.9% never rounds up to a reassuring "98%".
  return { text, partial: `from ${Math.floor(coverage * 100)}% of this window — gaps are not counted` };
}
